import { z } from 'zod';
import { optionalText, paginationQuerySchema, toPatchSchema, uuidSchema } from './common.js';
import { businessManagerFilterSchema, type BusinessManagerRef, type RecordRef } from './records.js';

const money = z.coerce.number().min(0).max(1e15);
const rate = z.coerce.number().min(0).max(100);

/**
 * The catalogue. Quotes, orders and invoices all price from here, which is why
 * `unitPrice` and `taxRate` are on the product rather than repeated per
 * document — a line item copies them at the moment it is added, so a later
 * price change does not silently rewrite history.
 */
export const createProductSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(200),
  sku: optionalText(60),
  description: optionalText(4000),
  unitPrice: money.default(0),
  taxRate: rate.default(0),
  category: optionalText(120),
  isActive: z.coerce.boolean().default(true),

  vendorId: uuidSchema.nullish(),
  businessManagerId: uuidSchema.optional(),
  customFields: z.record(z.string(), z.unknown()).optional(),
});
export type CreateProductInput = z.infer<typeof createProductSchema>;

export const updateProductSchema = toPatchSchema(createProductSchema);
export type UpdateProductInput = z.infer<typeof updateProductSchema>;

export const PRODUCT_SORT_FIELDS = ['createdAt', 'updatedAt', 'name', 'sku', 'unitPrice'] as const;

export const listProductsQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(PRODUCT_SORT_FIELDS).default('name'),
  businessManagerId: businessManagerFilterSchema.optional(),
  category: z.string().trim().max(120).optional(),
  vendorId: uuidSchema.optional(),
  /** Only sellable products — the default for any picker. */
  activeOnly: z.stringbool().optional(),
});
export type ListProductsQuery = z.infer<typeof listProductsQuerySchema>;

export interface ProductSummary {
  id: string;
  name: string;
  sku: string | null;
  unitPrice: string;
  currency: string;
  taxRate: string;
  category: string | null;
  isActive: boolean;
  vendor: RecordRef | null;
  businessManager: BusinessManagerRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProductDetail extends ProductSummary {
  description: string | null;
  customFields: Record<string, unknown>;
}
